"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { motion, AnimatePresence } from "framer-motion"
import {
  DownloadIcon as FiDownload,
  FileSearchIcon as FiSearch,
  FilterIcon as FiFilter,
  Trash2Icon as FiTrash2,
  RefreshCwIcon as FiRefreshCw,
  ChevronLeftIcon as FiChevronLeft,
  ChevronRightIcon as FiChevronRight,
  CheckIcon as FiCheck,
  EyeIcon as FiEye,
  XIcon as FiX,
  Sparkles,
  CreditCardIcon as FiCreditCard,
} from "lucide-react"
import { verifyKycAPI, updatePaymentStatus, toggleStartupStatus } from "../utils/api"

const StartupTable = () => {
  const [startups, setStartups] = useState([])
  const [founderKycs, setFounderKycs] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [kycDialogOpen, setKycDialogOpen] = useState(false)
  const [verifyConfirmDialogOpen, setVerifyConfirmDialogOpen] = useState(false)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [selectedDocId, setSelectedDocId] = useState(null)
  const [selectedKycFounderId, setSelectedKycFounderId] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedStartupId, setSelectedStartupId] = useState(null)
  const [foundersForSelectedStartup, setFoundersForSelectedStartup] = useState([])
  const [founderToVerify, setFounderToVerify] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState("Unpaid")
  const [transactionId, setTransactionId] = useState("")
  const recordsPerPage = 20

  // Fetch startups and founder KYC records
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suRes, kycRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/startups`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/founder-kyc`),
        ])
        setStartups(suRes.data)
        setFounderKycs(Array.isArray(kycRes.data.data) ? kycRes.data.data : [])
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }
    fetchData()
  }, [])

  // Merge KYC into startups by startupId
  const mergedRows = startups.map((s) => {
    const startupFounders = founderKycs.filter((f) => f.startupId === s.startupId)
    const hasPendingKyc = startupFounders.some((f) => f.verification_status === "pending")
    const allApproved = startupFounders.length > 0 && startupFounders.every((f) => f.verification_status === "approved")

    let kycStatus = "No KYC"
    if (startupFounders.length > 0) {
      if (allApproved) {
        kycStatus = "All Approved"
      } else if (hasPendingKyc) {
        kycStatus = "Pending"
      } else {
        kycStatus = "Partially Approved"
      }
    }

    return {
      ...s,
      founderKycs: startupFounders,
      kycStatus,
      paymentStatus: s.paymentStatus || "Unpaid"
    }
  })

  // Toggle payment status
  const togglePaymentStatus = async (startupId, currentStatus) => {
    if (currentStatus === "Paid") return;

    try {
      const newStatus = "Paid";

      await updatePaymentStatus(startupId, {
        paymentStatus: newStatus,
        status: "active"
      });

      // Update local state
      setStartups(prev =>
        prev.map(s =>
          s._id === startupId
            ? { ...s, paymentStatus: newStatus, status: "active" }
            : s
        )
      );
    } catch (error) {
      console.error("Error updating payment status:", error);
    }
  };

  // Handlers for actions
  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/startups/${selectedDocId}`)
      setDeleteDialogOpen(false)
      setStartups((prev) => prev.filter((s) => s._id !== selectedDocId))
    } catch (error) {
      console.error("Error deleting startup:", error)
    }
  }

  // const handleStatusToggle = (docId, currentStatus) => {
  //   setSelectedDocId(docId)
  //   setSelectedStatus(currentStatus)
  //   setStatusDialogOpen(true)
  // }

  const handleStatusToggle = async (docId, currentStatus) => {
    try {
      const action = currentStatus === "Active" ? "hold" : "activate";
      await toggleStartupStatus(docId, action);

      // Update local state
      setStartups(prev =>
        prev.map(s =>
          s._id === docId
            ? { ...s, status: action === "hold" ? "Hold" : "Active" }
            : s
        )
      );
    } catch (error) {
      console.error("Error changing status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleStatusConfirm = async () => {
    try {
      const newStatus = selectedStatus === "Active" ? "Hold" : "Active"
      const endpoint = newStatus === "Hold" ? "hold" : "activate"
      await axios.put(`${process.env.REACT_APP_API_URL}/api/startups/${endpoint}/${selectedDocId}`)
      setStartups((prev) => prev.map((s) => (s._id === selectedDocId ? { ...s, status: newStatus } : s)))
      setStatusDialogOpen(false)
    } catch (error) {
      console.error("Error changing status:", error)
    }
  }

  const handleDownloadPitchDeck = (pitchDeck) => {
    if (pitchDeck) window.open(`${pitchDeck}`, "_blank")
  }

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(mergedRows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Startups")
    XLSX.writeFile(wb, "Startup_List.xlsx")
  }

  const handleVerifyClick = (row) => {
    setSelectedStartupId(row.startupId)
    const startupFounders = founderKycs.filter((f) => f.startupId === row.startupId)
    setFoundersForSelectedStartup(startupFounders)
    setKycDialogOpen(true)
  }

  const handleVerifyFounderClick = (founder) => {
    setFounderToVerify(founder)
    setVerifyConfirmDialogOpen(true)
  }

  const handleKycVerifyConfirm = async () => {
    try {
      const { data } = await verifyKycAPI(founderToVerify.founderId)

      // Update the main founder KYC list
      setFounderKycs((prev) =>
        prev.map((f) =>
          f.founderId === founderToVerify.founderId ? { ...f, verification_status: data.data.verification_status } : f,
        ),
      )

      // Update the founders list in the dialog
      setFoundersForSelectedStartup((prev) =>
        prev.map((f) =>
          f.founderId === founderToVerify.founderId ? { ...f, verification_status: data.data.verification_status } : f,
        ),
      )

      setVerifyConfirmDialogOpen(false)
      setFounderToVerify(null)
    } catch (error) {
      console.error("Error verifying KYC:", error)
    }
  }

  const handlePaymentStatusUpdate = async () => {
    try {
      const paymentData = {
        paymentStatus,
        ...(transactionId && { transactionId })
      };

      await updatePaymentStatus(selectedDocId, paymentData);

      // Update local state
      setStartups(prev =>
        prev.map(s =>
          s._id === selectedDocId
            ? { ...s, paymentStatus, transactionId }
            : s
        )
      );

      setPaymentDialogOpen(false);
      setPaymentStatus("Unpaid");
      setTransactionId("");
    } catch (error) {
      console.error("Error updating payment status:", error);
    }
  };

  // Search & filter
  const filtered = mergedRows.filter((item) => {
    const term = searchTerm.toLowerCase()
    if (filter) return item[filter]?.toString().toLowerCase().includes(term)
    return Object.values(item).some((v) => v?.toString().toLowerCase().includes(term))
  })

  const totalPages = Math.ceil(filtered.length / recordsPerPage)
  const displayed = filtered.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)

  const getKycStatusChip = (row) => {
    if (row.founderKycs.length === 0) {
      return <span className="text-gray-500 text-sm">No KYC</span>
    }

    switch (row.kycStatus) {
      case "All Approved":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <FiCheck className="w-3 h-3 mr-1" /> All Approved
          </span>
        )
      case "Pending":
      case "Partially Approved":
        return (
          <button
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200 transition-colors"
            onClick={() => handleVerifyClick(row)}
          >
            <FiEye className="w-4 h-4 mr-1" /> Verify (
            {row.founderKycs.filter((f) => f.verification_status === "pending").length} pending)
          </button>
        )
      default:
        return <span className="text-gray-500 text-sm">No KYC</span>
    }
  }

  const getPaymentStatusChip = (status, startupId) => {
    const statusMap = {
      'Paid': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        cursor: 'cursor-default'
      },
      'Unpaid': {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        cursor: 'cursor-pointer hover:bg-red-200'
      },
    };

    return (
      <span
        onClick={() => status === "Unpaid" && togglePaymentStatus(startupId, status)}
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMap[status]?.bg || 'bg-gray-100'} ${statusMap[status]?.text || 'text-gray-800'} ${statusMap[status]?.border || 'border-gray-200'} ${statusMap[status]?.cursor || 'cursor-default'}`}
      >
        {status}
        {status === "Unpaid" && (
          <FiRefreshCw className="w-3 h-3 ml-1" />
        )}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      {/* Background Pattern */}
      <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fillRule="evenodd"%3E%3Cg fill="%239C92AC" fillOpacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-40 pointer-events-none' />
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
        >
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Sparkles className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Startup Registration
                </h1>
                <p className="text-gray-600 text-lg">Manage and track startup applications</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownloadExcel}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 text-lg"
            >
              <FiDownload className="text-xl" />
              Download Excel
            </motion.button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <FiFilter className="text-purple-600 text-xl" />
            <h2 className="text-xl font-semibold text-gray-800">Search & Filter</h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-4 w-full">
            <div className="flex-1 space-y-2">
              <label htmlFor="filter-by" className="text-sm font-semibold text-gray-700">
                Filter By
              </label>
              <select
                id="filter-by"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
              >
                <option value="">All</option>
                <option value="startupName">Startup Name</option>
                <option value="fullName">Founder Name</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="industry">Industry</option>
                <option value="stage">Stage</option>
                <option value="location">Location</option>
                <option value="paymentStatus">Payment Status</option>
              </select>
            </div>
            <div className="flex-1 space-y-2">
              <label htmlFor="search-term" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FiSearch className="text-base" />
                Search Term
              </label>
              <input
                id="search-term"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search startups..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
              />
            </div>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8 overflow-x-auto"
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Date",
                  "Time",
                  "Startup Name",
                  "Founder Name",
                  "Email",
                  "Phone",
                  "Industry",
                  "Stage",
                  "Location",
                  "Status",
                  "Pitch Deck",
                  "KYC Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12 text-center text-gray-500 text-lg">
                    No startups found. Try adjusting your search or filter criteria.
                  </td>
                </tr>
              ) : (
                displayed.map((row) => {
                  const dt = new Date(row.createdAt)
                  const date = dt.toLocaleDateString()
                  const time = dt.toLocaleTimeString()
                  return (
                    <tr key={row._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{date}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{time}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {row.startupName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.fullName}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.phone}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.industry}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.stage}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.location}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.status === "Active"
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                            }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.pitchDeck ? (
                          <button
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                            onClick={() => handleDownloadPitchDeck(row.pitchDeck)}
                          >
                            <FiDownload className="w-4 h-4 mr-1" /> Download
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{getKycStatusChip(row)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-white shadow-md hover:shadow-lg transition-all duration-200 ${row.status === "Active"
                                ? "bg-gradient-to-r from-orange-600 to-amber-600"
                                : "bg-gradient-to-r from-green-600 to-emerald-600"
                              }`}
                            onClick={() => handleStatusToggle(row._id, row.status)}
                          >
                            <FiRefreshCw className="w-4 h-4 mr-1" />
                            {row.status === "Active" ? "Hold" : "Activate"}
                          </button>
                          <button
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => {
                              setSelectedDocId(row._id)
                              setDeleteDialogOpen(true)
                            }}
                          >
                            <FiTrash2 className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </motion.div>

        {/* Pagination */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((cp) => cp - 1)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
              }`}
          >
            <FiChevronLeft className="text-xl" />
            Previous
          </motion.button>
          <div className="flex items-center gap-2">
            <span className="text-base text-gray-600">
              Page <span className="font-semibold text-purple-600">{currentPage}</span> of{" "}
              <span className="font-semibold text-purple-600">{totalPages || 1}</span>
            </span>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage((cp) => cp + 1)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${currentPage === totalPages || totalPages === 0
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
              }`}
          >
            Next
            <FiChevronRight className="text-xl" />
          </motion.button>
        </motion.div>
      </div>

      {/* Delete Dialog */}
      <AnimatePresence>
        {deleteDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FiTrash2 className="text-red-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>
                <p className="text-gray-700 text-base mb-6">Are you sure you want to delete this record?</p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setDeleteDialogOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDeleteConfirm}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 text-base"
                  >
                    Yes, Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* KYC Verification Dialog */}
      <AnimatePresence>
        {kycDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Founder KYC Verification
                  </h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setKycDialogOpen(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                    <FiX className="text-gray-600 hover:text-red-600" />
                  </motion.button>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Founder Name
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Founder Id
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Email
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Phone
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Aadhar
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          PAN
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          DIN
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {foundersForSelectedStartup.map((founder) => (
                        <tr key={founder._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.founderName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.founderId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.email}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.phoneNumber}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.aadharNumber}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.panNumber}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{founder.DIN}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {founder.verification_status === "approved" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                Approved
                              </span>
                            ) : founder.verification_status === "pending" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                Pending
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                Rejected
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {founder.verification_status === "pending" && (
                              <button
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                                onClick={() => handleVerifyFounderClick(founder)}
                              >
                                Verify
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-6 border-t border-gray-100 flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setKycDialogOpen(false)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Verify Confirmation Dialog */}
      <AnimatePresence>
        {verifyConfirmDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FiCheck className="text-green-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Confirm KYC Verification</h3>
                    <p className="text-sm text-gray-600">This action will mark the KYC as approved.</p>
                  </div>
                </div>
                {founderToVerify && (
                  <p className="text-gray-700 text-base mb-6">
                    Are you sure you want to verify the KYC for founder{" "}
                    <strong className="text-gray-900">{founderToVerify.founderName}</strong>?
                  </p>
                )}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setVerifyConfirmDialogOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleKycVerifyConfirm}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 text-base"
                  >
                    Confirm Verification
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Status Dialog */}
      <AnimatePresence>
        {paymentDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FiCreditCard className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Update Payment Status</h3>
                    <p className="text-sm text-gray-600">Change the payment status for this startup</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    >
                      <option value="Paid">Paid</option>
                      <option value="Unpaid">Unpaid</option>
                      <option value="Pending">Pending</option>
                      <option value="Failed">Failed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID (Optional)</label>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Enter transaction ID"
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setPaymentDialogOpen(false);
                      setPaymentStatus("Unpaid");
                      setTransactionId("");
                    }}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePaymentStatusUpdate}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 text-base"
                  >
                    Update Status
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default StartupTable;